import React, {ReactElement, useEffect, useState} from 'react';
import ErrorAlertComponent from '../../../../components/ErrorAlert.component';
import Group from '../../../../@types/group';
import GroupService from '../../../../services/group.service';
import {Button, FormGroup, MenuItem, Select, Stack, TextField, Typography} from '@mui/material';
import {useWorkspaceContext} from '../../../../context/workspaceContext';
import {addOrUpdateGroup} from '../../../../utils/group-utils';
import {ResourceType} from '../../../../@types/resource-type';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {getResourceNameWithAscendants} from '../../../../utils/resource-utils';
import {uuid} from '../../../../utils/utils';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import {ResourceAction} from '../../../../@types/resource-action';
import {GroupEditorProps} from '../../../../@types/editor-props';
import authService from '../../../../services/auth.service';


export default function GroupEditor(props: GroupEditorProps): ReactElement {
    const { group, isActive, updateTab } = props;
    const { workspaceObjects, setWorkspaceObjects, currentAction,
        setCurrentAction, setCurrentWorkspaceObject, addOrUpdateAdditionalResource,
        getAdditionalResource, publishResourceUpdate } = useWorkspaceContext();
    const [name, setName] = useState<string>(group?.name || '');
    const [description, setDescription] = useState<string>(group?.description || '');
    const [error, setError] = useState<string|null>(null);
    const [parentGroup, setParentGroup] = React.useState(group?.parent ? String(group.parent) : '0');
    const uuidRef = React.useRef(uuid());

    const parentGroupChange = (event: any): void => {
        setParentGroup(event.target.value as string);
    };

    const onNameChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        setName(event.target.value);
    };

    const onDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        setDescription(event.target.value);
    };

    const formGroup = (): Group => {
        const grpData = new Group();
        grpData.name = name ?? '';
        grpData.description = description;

        if (group && group.id) {
            grpData.id = group.id;
        }
        if (parentGroup != '0') {
            grpData.parent = +parentGroup;
        } else {
            grpData.parent = undefined;
        }
        return grpData;
    }

    const updateGroup = (grp: Group): void => {
        let groupObject = {objectType: ResourceType.GROUPS, object: grp};
        setCurrentWorkspaceObject(groupObject);
        
        // Publish update event for other components (only if group has an id)
        if (grp.id) {
            publishResourceUpdate({
                type: ResourceType.GROUPS,
                id: grp.id,
                updatedData: grp,
                source: 'save'
            });
        }
        
        if (workspaceObjects?.objectType === ResourceType.GROUPS) {
            let resources = workspaceObjects.resources ? workspaceObjects.resources : getAdditionalResource(workspaceObjects?.objectType).resources;
            setWorkspaceObjects({ objectType: ResourceType.GROUPS, resources: addOrUpdateGroup(grp, resources as Group[]) });
            addOrUpdateAdditionalResource({ objectType: ResourceType.GROUPS, resources: addOrUpdateGroup(grp, resources as Group[]) });
        } else {
            let resourcesObject = getAdditionalResource(workspaceObjects?.objectType);
            if (resourcesObject && resourcesObject.resources) {
                let resources = [...resourcesObject.resources];
                resources.forEach((resource) => {
                    if (resource.group?.id === grp.id) {
                        resource.group = grp;
                    }
                });
                setWorkspaceObjects({ objectType: workspaceObjects?.objectType ?? ResourceType.GROUPS, resources: resources });
            } else {
                setWorkspaceObjects({ objectType: workspaceObjects?.objectType ?? ResourceType.GROUPS});
            }
            addOrUpdateAdditionalResource({ objectType: ResourceType.GROUPS, resources: addOrUpdateGroup(grp, groups) });
        }
        updateTab(groupObject);
    }

    const onClickSave = (_event: React.SyntheticEvent): void => {
        saveGroup();
    }

    const saveGroup = (): void => {
        if (name) {
            setError(null);
            if (group.id) {
                const grpData = formGroup();
                // Create a custom object for patching that can include null parent
                const patchData: any = {
                    id: grpData.id,
                    name: grpData.name,
                    description: grpData.description
                };
                
                // Explicitly set parent to null when moving to top level
                if (parentGroup === '0') {
                    patchData.parent = null;
                } else if (parentGroup !== '0') {
                    patchData.parent = +parentGroup;
                }
                
                // Use the patchData object instead of the Group instance
                GroupService.patchGroup(patchData as Group)
                    .then((grp: Group) => {
                        updateGroup(grp);
                    })
                    .catch((err: Error) => {
                        setError(err.message);
                    });
            } else {
                GroupService.createGroup(formGroup())
                    .then((grp: Group) => {
                        updateGroup(grp);
                    })
                    .catch((err: Error) => {
                        setError(err.message);
                    });
            }
        } else {
            setError('Working area name is required');
        }
    };

    // Track the current group to detect when editing a different group
    // Use a unique key that combines id and parent to handle both new and existing groups
    const currentGroupKeyRef = React.useRef(`${group?.id || 'new'}-${group?.parent || '0'}`);

    // issue 1443 indicates that the new explorer can enter a state where 
    // the GroupEditor is rerendering recursively and making calls to the group API.
    // The recursive re-render is most likely caused by the useEffect that calls 
    // setCurrentAction(null) and saveGroup() when currentAction === ResourceAction.SAVE 
    // This state update triggers a re-render, which can cause the effect to run again. 
    // The fix for this issue is to use a local flag that ensures the saveGroup() function is only 
    // called once per group edit to prevent repeated saves.
    const hasSavedRef = React.useRef(false);
    
    useEffect(() => {
        if (group) {
            // Create a unique key for the current group
            const currentKey = `${group.id || 'new'}-${group.parent || '0'}`;
            
            // Only reset form values if we're editing a different group
            if (currentGroupKeyRef.current !== currentKey) {
                currentGroupKeyRef.current = currentKey;
                hasSavedRef.current = false; // Reset flag when switching group

                // Reset all form values when switching to a different group
                setName(group.name || '');
                setDescription(group.description || '');
                setParentGroup(group.parent ? String(group.parent) : '0');
            }
            
            if (isActive && currentAction === ResourceAction.SAVE && !hasSavedRef.current) {
                hasSavedRef.current = true; // Prevent repeated saves
                setCurrentAction(null);
                saveGroup();
            }
        }
        // eslint-disable-next-line
    }, [group, currentAction, isActive]);

    let groups = getAdditionalResource(ResourceType.GROUPS).resources as Group[] || [];
    groups.sort((a, b) => a.name.localeCompare(b.name))
    
    // Determine if user can save the group
    const canSaveGroup = (): boolean => {
        if (group.id) {
            // Existing group: check if user can edit
            return group.canEdit();
        } else {
            // New group
            if (parentGroup === '0') {
                // Top-level group: only super users can create
                return authService.isSuperUser();
            } else {
                // Subgroup: check if user has admin role in parent group
                const selectedParentGroup = groups.find(g => g.id === Number(parentGroup));
                return selectedParentGroup ? (selectedParentGroup.currentUser?.role === 'ADMIN' || authService.isSuperUser()) : false;
            }
        }
    };
    
    // Get helper text for disabled state
    const getDisabledReason = (): string => {
        if (!name) {
            return 'Working area name is required';
        }
        if (group.id) {
            return 'You do not have permission to edit this working area';
        } else {
            if (parentGroup === '0') {
                return 'Only super users can create top-level working areas';
            } else {
                return 'You do not have admin permission in the selected parent working area';
            }
        }
    };

    return (
        <FormGroup className='formStyle'>
            <Stack className='formControl'>
                <TextField
                    label='Working Area Name'
                    aria-label='working area name'
                    type='text'
                    variant='outlined'
                    size='small'
                    id={`name-input-${uuidRef.current}`}
                    autoComplete='on'
                    value={name || ''}
                    onChange={onNameChange}
                    sx={{
                        '& input': {
                            border: 'none',
                        }
                    }}
                    slotProps={{
                        input: {
                            'role': 'textbox',
                            'tabIndex': 0,
                            'aria-labelledby': `name-input-${uuidRef.current}`,
                            'aria-label': 'working area name'
                        }
                    }}
                />
                <hr/>
                <TextField
                    label='Working Area Description (Optional)'
                    aria-label='working area description'
                    type='text'
                    variant='outlined'
                    size='small'
                    id={`group-description-${uuidRef.current}`}
                    autoComplete='on'
                    value={description || ''}
                    onChange={onDescriptionChange}
                    sx={{
                        '& input': {
                            border: 'none',
                        }
                    }}
                    slotProps={{
                        input: {
                            'role': 'textbox',
                            'tabIndex': 0,
                            'aria-labelledby': `group-description-${uuidRef.current}`,
                            'aria-label': 'working area description'
                        }
                    }}
                />
                <hr />
                <FormControl>
                    <InputLabel tabIndex={-1}  id={`select-label-${uuidRef.current}`} htmlFor={`select-list-${uuidRef.current}`}>Parent Working Area</InputLabel>
                    <Select
                        role='presentation'
                        labelId={`select-label-${uuidRef.current}`}
                        id={`parent-group-label-${uuidRef.current}`}
                        size='small'
                        label='Parent Working Area'
                        value={parentGroup || '0'}
                        onChange={parentGroupChange}
                        inputProps={{
                            'role': 'textbox',
                            'tabIndex': 0,
                            'id': `select-list-${uuidRef.current}`,
                        }}
                    >
                        <MenuItem value={0} dense>--Top Level--</MenuItem>
                        {groups.filter((grp) => grp.id != group.id).map((group) => (
                            <MenuItem key={group.id} value={group.id} dense>{getResourceNameWithAscendants(group, ResourceType.GROUPS)}</MenuItem>
                        ))}
                    </Select>

                </FormControl>
                <hr />
                <div>
                    <Button 
                        className='formBtn' 
                        id={`save-group-${uuidRef.current}`}
                        size='small' 
                        variant='contained'
                        aria-label='save working area'
                        title={(!name || !canSaveGroup()) ? getDisabledReason() : 'Save Working Area'}
                        onClick={onClickSave}
                        startIcon={<SaveAltIcon />}
                        disabled={!name || !canSaveGroup()}
                    >
                        Save
                    </Button>
                    {(!name || !canSaveGroup()) && (
                        <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            display="block" 
                            sx={{ mt: 1 }}
                        >
                            {getDisabledReason()}
                        </Typography>
                    )}
                </div>
                {error ? <ErrorAlertComponent message={error} />  : null}
            </Stack>
        </FormGroup>
    );
}